import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  AlertTriangle,
  ArrowRight,
  Phone,
  Search,
  Heart,
  Brain,
  Shield,
  Activity,
  Smartphone,
  Monitor,
  ShoppingBag,
  Gamepad2,
  Users
} from "lucide-react";
import { Header, Footer } from "../../../components/Layout";

const ConductualesPage = () => {
  const addictionTypes = [
    {
      name: "Adicción a Redes Sociales",
      icon: Smartphone,
      description: "Uso compulsivo de Instagram, TikTok, Facebook. Necesidad constante de revisar notificaciones y buscar validación.",
      signs: ["Revisar el celular cada pocos minutos", "Ansiedad al no tener acceso", "Comparación constante con otros", "Afecta el sueño"],
      color: "from-blue-500 to-cyan-600",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/30"
    },
    {
      name: "Adicción a Videojuegos",
      icon: Gamepad2,
      description: "Jugar de forma incontrolable, priorizando el juego sobre responsabilidades. Reconocida por la OMS como trastorno.",
      signs: ["Jugar 6+ horas diarias", "Irritabilidad al no jugar", "Descuidar higiene y comidas", "Aislamiento social real"],
      color: "from-purple-500 to-violet-600",
      bgColor: "bg-purple-500/10",
      borderColor: "border-purple-500/30"
    },
    {
      name: "Adicción a Pornografía",
      icon: Monitor,
      description: "Consumo compulsivo que afecta relaciones, sexualidad y percepción de la realidad. Cada vez más común en jóvenes.",
      signs: ["Necesidad de contenido más extremo", "Afecta relaciones reales", "Uso a pesar de querer parar", "Interferencia con la vida"],
      color: "from-red-500 to-rose-600",
      bgColor: "bg-red-500/10",
      borderColor: "border-red-500/30"
    },
    {
      name: "Compras Compulsivas",
      icon: ShoppingBag,
      description: "Oniomanía o adicción a comprar. Compras innecesarias para sentir alivio temporal, seguido de culpa.",
      signs: ["Compras para sentirse mejor", "Ocultar compras a la familia", "Deudas por compras", "Artículos sin usar"],
      color: "from-amber-500 to-orange-600",
      bgColor: "bg-amber-500/10",
      borderColor: "border-amber-500/30"
    }
  ];

  const commonFactors = [
    "Dopamina: Estas conductas activan los mismos circuitos de recompensa que las drogas",
    "Escape emocional: Se usan para evitar emociones negativas o estrés",
    "Accesibilidad: Disponibles 24/7, sin costo aparente ni estigma social",
    "Diseño adictivo: Aplicaciones y juegos diseñados para maximizar el enganche"
  ];

  const treatmentOptions = [
    {
      title: "Terapia Cognitivo-Conductual",
      description: "Muy efectiva para adicciones conductuales. Trabaja pensamientos, emociones y comportamientos.",
      icon: Brain
    },
    {
      title: "Digital Detox Estructurado",
      description: "Reducción gradual del uso con límites claros. Uso de apps de control parental para adultos.",
      icon: Smartphone
    },
    {
      title: "Terapia de Grupo",
      description: "Compartir experiencias con otros que enfrentan el mismo problema reduce la vergüenza y aislamiento.",
      icon: Users
    },
    {
      title: "Tratamiento de Causa Raíz",
      description: "Abordar ansiedad, depresión, trauma u otras condiciones que subyacen a la conducta adictiva.",
      icon: Heart
    }
  ];

  const selfHelpStrategies = [
    "Establecer horarios específicos para el uso de tecnología",
    "Desactivar notificaciones no esenciales",
    "Crear zonas libres de dispositivos (dormitorio, comedor)",
    "Encontrar actividades alternativas satisfactorias",
    "Usar apps de seguimiento de tiempo de pantalla",
    "Practicar mindfulness para manejar la urgencia de uso"
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalCondition",
    "name": "Adicciones Conductuales",
    "alternateName": ["Adicciones sin sustancias", "Adicciones comportamentales"],
    "description": "Las adicciones conductuales son patrones de comportamiento compulsivo que activan los mismos circuitos cerebrales que las drogas."
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Adicciones", "item": "https://sinadicciones.org/adicciones" },
      { "@type": "ListItem", "position": 3, "name": "Conductuales", "item": "https://sinadicciones.org/adicciones/conductuales" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Adicciones Conductuales: Tecnología, Redes Sociales, Videojuegos | Sin Adicciones</title>
        <meta name="description" content="Información sobre adicciones conductuales: redes sociales, videojuegos, pornografía, compras compulsivas. Señales de alerta y opciones de tratamiento." />
        <link rel="canonical" href="https://sinadicciones.org/adicciones/conductuales" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/adicciones/conductuales" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/adicciones" className="hover:text-emerald-400 transition-colors">
              Adicciones
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Adicciones Conductuales</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-5xl">📱</span>
              <div className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full">
                <span className="text-cyan-400 font-medium">Las adicciones del siglo XXI</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Adicciones{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400">
                Conductuales
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl">
              Tecnología, redes sociales, videojuegos, pornografía, compras... 
              No involucran sustancias, pero activan los mismos circuitos cerebrales de recompensa. 
              Y están creciendo exponencialmente.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/profesionales"
                className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Buscar Especialista
              </Link>
              <Link 
                to="/herramientas"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Activity className="w-5 h-5" />
                Tests de Evaluación
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Behavioral Addictions */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Tipos de Adicciones Conductuales
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Estas son las adicciones comportamentales más comunes en la actualidad.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {addictionTypes.map((addiction, index) => (
              <div 
                key={index}
                className={`p-6 rounded-2xl ${addiction.bgColor} border ${addiction.borderColor}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${addiction.color} flex items-center justify-center flex-shrink-0`}>
                    <addiction.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{addiction.name}</h3>
                    <p className="text-slate-400 text-sm mt-1">{addiction.description}</p>
                  </div>
                </div>
                
                <div className="space-y-2 mt-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Señales de alerta:</p>
                  <ul className="grid grid-cols-2 gap-2">
                    {addiction.signs.map((sign, idx) => (
                      <li key={idx} className="text-slate-300 text-sm flex items-start gap-2">
                        <AlertTriangle className="w-3 h-3 mt-1 text-yellow-400 flex-shrink-0" />
                        <span>{sign}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why They're Addictive */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              ¿Por Qué Son Adictivas?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Las adicciones conductuales comparten mecanismos cerebrales con las adicciones a sustancias.
            </p>
          </div>

          <div className="space-y-4">
            {commonFactors.map((factor, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50"
              >
                <Brain className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-slate-300">{factor}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Self-Help Strategies */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-gradient-to-br from-emerald-900/30 to-slate-800/50 rounded-3xl p-8 md:p-12 border border-emerald-500/20">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">
                Estrategias de Autoayuda
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Pasos que puedes empezar a implementar hoy para reducir el uso problemático.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {selfHelpStrategies.map((strategy, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl"
                >
                  <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-400 font-bold text-sm">{index + 1}</span>
                  </div>
                  <p className="text-slate-300">{strategy}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-slate-400 mb-4">¿Las estrategias no funcionan?</p>
              <Link 
                to="/profesionales"
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all"
              >
                Buscar Ayuda Profesional
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Tratamiento Profesional
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Cuando la autoayuda no es suficiente, estas son las opciones de tratamiento más efectivas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {treatmentOptions.map((option, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50 hover:border-emerald-500/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <option.icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{option.title}</h3>
                    <p className="text-slate-400">{option.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Parents */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-slate-800/50 rounded-3xl p-8 md:p-12 border border-slate-700/50">
            <div className="flex items-center gap-3 mb-6">
              <Users className="w-8 h-8 text-cyan-400" />
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Para Padres
              </h2>
            </div>
            
            <p className="text-slate-300 mb-6">
              Si sospechas que tu hijo tiene una adicción conductual, estos son algunos pasos recomendados:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-bold text-white mb-4">Qué hacer:</h3>
                <ul className="space-y-3">
                  {[
                    "Abrir diálogo sin juzgar ni castigar",
                    "Establecer límites claros con amor",
                    "Modelar el uso saludable de tecnología",
                    "Buscar actividades alternativas en familia",
                    "Considerar ayuda profesional temprana"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-300">
                      <ArrowRight className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-bold text-white mb-4">Qué evitar:</h3>
                <ul className="space-y-3">
                  {[
                    "Quitar dispositivos abruptamente",
                    "Usar la tecnología como castigo/premio",
                    "Ignorar el problema esperando que pase",
                    "Criticar o avergonzar al joven",
                    "No establecer límites por miedo al conflicto"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-300">
                      <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-900/50 to-slate-800/50 border border-emerald-500/20 p-8 md:p-12 text-center">
            <Heart className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              La Tecnología No Tiene que Controlarte
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Es posible tener una relación saludable con la tecnología y las actividades que disfrutas. 
              Un profesional puede ayudarte a encontrar ese balance.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/asesoria"
                className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/25 flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Asesoría Experta
              </Link>
              <Link 
                to="/profesionales"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700"
              >
                Buscar Especialista
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Contenido Relacionado</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Ludopatía", path: "/adicciones/ludopatia", icon: "🎰" },
              { title: "Marihuana", path: "/adicciones/marihuana", icon: "🌿" },
              { title: "Terapia Cognitivo-Conductual", path: "/tratamiento-cognitivo-conductual", icon: "🧠" },
              { title: "Tratamiento Ambulatorio", path: "/tratamiento-ambulatorio", icon: "🏥" }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-emerald-500/30 transition-all group"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-white group-hover:text-emerald-400 transition-colors">{item.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ConductualesPage;
